import { useEffect, useMemo, useState } from "react";
import "./booking-context-intro.css";
import "./concierge-start-navigation.css";
import "./booking-person-selector.css";
import { bookingPersonRequest, otherPersonSiteUrl } from "./lib/bookingHost";
import { type BookingResource, selectBookingResource } from "./lib/bookingPeople";
import { loadBookingPeopleContext } from "./lib/bookingPeopleRepository";
import { KATRIN_PORTRAIT_PATH, RAYK_PORTRAIT_PATH } from "./lib/portrait";
import { PersonPortrait } from "./PersonPortrait";

const RAYK_FALLBACK: BookingResource = {
  id: "rayk-fallback",
  slug: "rayk",
  name: "Rayk Schörling",
  timezone: "Europe/Berlin",
  offerIds: [],
};

const KATRIN_FALLBACK: BookingResource = {
  id: "katrin-fallback",
  slug: "katrin",
  name: "Katrin Schörling",
  timezone: "Europe/Berlin",
  offerIds: [],
};

function fallbackResourceFor(slug: string | null) {
  return slug === "katrin" ? KATRIN_FALLBACK : RAYK_FALLBACK;
}

export function AppointmentEntry() {
  const bookingRequest = bookingPersonRequest();
  const requestedPerson = bookingRequest.slug;
  const fallbackResource = fallbackResourceFor(requestedPerson);
  const [resources, setResources] = useState<BookingResource[]>([fallbackResource]);
  const [peopleReady, setPeopleReady] = useState(false);

  useEffect(() => {
    let mounted = true;

    loadBookingPeopleContext()
      .then((context) => {
        if (!mounted) return;
        const nextResources = context.resources.filter((resource) => resource.offerIds.length > 0);
        setResources(nextResources.length > 0 ? nextResources : [fallbackResource]);
        setPeopleReady(true);
      })
      .catch((error) => {
        console.warn("Booking people context could not be loaded.", error);
        if (!mounted) return;
        setResources([fallbackResource]);
        setPeopleReady(true);
      });

    return () => {
      mounted = false;
    };
  }, [fallbackResource]);

  const selectedResource = useMemo(
    () => selectBookingResource(resources, requestedPerson),
    [requestedPerson, resources],
  );

  if (peopleReady && !selectedResource && (resources.length > 1 || requestedPerson)) {
    return <PersonSelector resources={resources} />;
  }

  const resource = selectedResource ?? fallbackResource;
  const firstName = resource.name.split(/\s+/)[0] || "Rayk";
  const includePerson = peopleReady && resources.length > 1 && !bookingRequest.fixedByHost;
  const isRayk = resource.slug === "rayk";

  return (
    <>
      <section
        className="booking-context booking-context--appointments"
        aria-labelledby="appointment-entry-title"
      >
        <div>
          <p>1:1 · Persönliche Arbeit</p>
          <h1 id="appointment-entry-title">
            {isRayk ? "Online oder in Berlin?" : "1:1 online"}
          </h1>
          <span>
            {isRayk
              ? "Beides ist persönliche 1:1-Arbeit. Online ist flexibel und ortsunabhängig. In Berlin wird die gemeinsame Präsenz selbst Teil des Prozesses. Danach siehst du Rahmen, Dauer, Preis und freie Termine."
              : "Persönliche 1:1-Arbeit mit Katrin findet derzeit online über Google Meet statt. Danach siehst du Dauer, Preis und die nächsten freien Termine."}
          </span>
        </div>

        <aside
          className="booking-context__presence booking-context__presence--appointments"
          aria-label={`Persönlicher Termin mit ${resource.name}`}
        >
          <PersonPortrait
            name={resource.name}
            initial={firstName.charAt(0).toUpperCase()}
            photoSrc={isRayk ? RAYK_PORTRAIT_PATH : KATRIN_PORTRAIT_PATH}
          />
          <div className="booking-context__facts">
            <span>Online · Google Meet</span>
            <span>{isRayk ? "Berlin · ab 10. November" : "1:1 · online"}</span>
          </div>
        </aside>
      </section>

      {includePerson ? (
        <button className="booking-person__change" type="button" onClick={clearPersonSelection}>
          ← Andere Person wählen
        </button>
      ) : null}

      {bookingRequest.fixedByHost ? (
        <a className="booking-person__change" href={otherPersonSiteUrl(isRayk ? "rayk" : "katrin")}>
          {isRayk ? "Du suchst Katrin? →" : "Du suchst Rayk? →"}
        </a>
      ) : null}

      <nav className="concierge-start concierge-start--appointments" aria-label="Terminart wählen">
        <div
          className={
            isRayk
              ? "concierge-start__choices concierge-start__choices--two"
              : "concierge-start__choices concierge-start__choices--one"
          }
        >
          <a
            className="concierge-start__choice is-online"
            href={appointmentHref("/online", resource.slug, includePerson)}
          >
            <span className="concierge-start__tag">01</span>
            <span className="concierge-start__copy">
              <strong>Online</strong>
              <small>
                {isRayk
                  ? "1:1 über Google Meet. Laufend buchbar und unabhängig von deinem Ort."
                  : "1:1 über Google Meet. Persönlich, verbindlich und ortsunabhängig."}
              </small>
            </span>
            <span className="concierge-start__arrow" aria-hidden="true">
              →
            </span>
          </a>

          {isRayk ? (
            <a
              className="concierge-start__choice is-berlin"
              href={appointmentHref("/berlin", resource.slug, includePerson)}
            >
              <span className="concierge-start__tag">02</span>
              <span className="concierge-start__copy">
                <strong>Berlin</strong>
                <small>
                  1:1 in Berlin-Weißensee. Wenn gemeinsamer Raum einen Unterschied macht.
                </small>
              </span>
              <span className="concierge-start__arrow" aria-hidden="true">
                →
              </span>
            </a>
          ) : null}
        </div>
      </nav>
    </>
  );
}

function PersonSelector({ resources }: { resources: BookingResource[] }) {
  return (
    <section className="booking-person" aria-labelledby="booking-person-title">
      <p>1:1 · Persönliche Arbeit</p>
      <h1 id="booking-person-title">Mit wem möchtest du arbeiten?</h1>
      <span>Wähle zuerst die Person. Danach siehst du ihre Formate, Orte und freien Termine.</span>
      <div className="booking-person__choices">
        {resources.map((resource, index) => (
          <a
            className="booking-person__choice"
            href={personSelectionHref(resource.slug)}
            key={resource.id}
          >
            <small>{String(index + 1).padStart(2, "0")}</small>
            <strong>{resource.name}</strong>
            <span>Persönliche Termine ansehen →</span>
          </a>
        ))}
      </div>
    </section>
  );
}

function appointmentHref(path: string, personSlug: string, includePerson: boolean) {
  if (!includePerson) return path;
  const url = new URL(path, window.location.origin);
  url.searchParams.set("person", personSlug);
  return `${url.pathname}${url.search}`;
}

function personSelectionHref(personSlug: string) {
  const url = new URL("/termine", window.location.origin);
  url.searchParams.set("person", personSlug);
  return `${url.pathname}${url.search}`;
}

function clearPersonSelection() {
  window.location.assign(new URL("/termine", window.location.origin));
}
